import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { ApiQueryParamsDTO } from './api-interface';
import { environment } from '../environments/environment';
import { SafeStorageService } from '../shared/safe-storage.service';
import { SEM_CONTEXTO_CONTADOR } from '../shared/contador-contexto.interceptor';
import { SessaoService } from './sessao/sessao.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private safe: SafeStorageService,
    private sessaoService: SessaoService,
  ) { }

  autenticar(email?: string, senha?: string): Observable<any> {
    return this.http
      .post<any>(
        `${this.baseUrl}/login`,
        {
          email: email,
          senha: senha,
          // Sessao unica por plataforma: o backend usa estes campos para saber qual vaga o login
          // ocupa (um navegador + um celular por conta) e para reconhecer ESTE navegador.
          plataforma: this.sessaoService.plataforma,
          deviceId: this.sessaoService.obterDeviceId(),
        },
        { observe: 'response' } // Observe the full response to access headers
      )
      .pipe(
        tap((response) => {
          // Extraia o token da resposta
          const token = response.headers.get('Authorization'); // ou `response.headers.get('Authorization')` se o token estiver no cabeçalho
          if (token) {
            // Armazene o token no sessionStorage ou sessionStorage
            this.safe.setItem('authToken', token);
          }
        })
      );
  }

  get(endpoint: string): Observable<any> {
    return this.http
      .get(`${this.baseUrl}${endpoint}`, httpOptions)
      .pipe(map((res) => res));
  }

  // filter<T>(endpoint: string, data: any): Observable<any> {
  //   return this.http.get(`${this.baseUrl}${endpoint}`, data, httpOptions).pipe(
  //     map((res) => res),
  //     catchError((error) => this.errorHandler(error))
  //   );
  // }

  errorHandler(error: any): Observable<never> {
    return throwError(() => error); // Propaga o erro original preservando error.error.message do backend
  }

  read<T>(endpoint: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${endpoint}`).pipe(
      map((res) => res),
      catchError((error) => {
        return this.errorHandler(error); // Propagação do erro para o subscribe
      })
    );
  }

  /**
   * GET identico ao `read`, mas marca a request com o token
   * `SEM_CONTEXTO_CONTADOR` — o `ContadorContextoInterceptor` a deixa intocada e
   * NAO injeta `X-Empresa-Contexto`. Usar quando a tela precisa dos dados da
   * PROPRIA empresa do contador numa rota que, por padrao, seria sequestrada
   * pelo contexto do cliente ativo (ex.: `/conta`).
   */
  readSemContextoContador<T>(endpoint: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${endpoint}`, {
      context: new HttpContext().set(SEM_CONTEXTO_CONTADOR, true),
    }).pipe(
      map((res) => res),
      catchError((error) => {
        return this.errorHandler(error); // Propagação do erro para o subscribe
      })
    );
  }

  /** GET com HttpParams (evita problemas de encoding manual com caracteres como / e :) */
  readWithParams<T>(endpoint: string, params: { [key: string]: string | number }): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    return this.http.get(`${this.baseUrl}${endpoint}`, { params: httpParams }).pipe(
      map((res) => res),
      catchError((error) => this.errorHandler(error))
    );
  }

  readText<T>(endpoint: string): Observable<string> {
    return this.http.get(`${this.baseUrl}${endpoint}`, { responseType: 'text' }).pipe(
      map((res) => res),
      catchError((error) => {
        return this.errorHandler(error); // Propagação do erro para o subscribe
      })
    );
  }

  /**
   * GET que devolve o corpo binario como `Blob` (responseType 'blob').
   * Usar para downloads de arquivos (ex.: .xlsx/.csv), onde o backend
   * responde binario com Content-Disposition attachment. O tratamento de
   * erro segue o padrao dos demais metodos (propaga o erro original); note
   * que, em resposta de erro, `error.error` vem como Blob, mas `error.status`
   * continua acessivel para 403/422/404.
   */
  readBlob(endpoint: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}${endpoint}`, { responseType: 'blob' }).pipe(
      catchError((error) => this.errorHandler(error))
    );
  }

  /**
   * Variante de `readText` que devolve o `HttpResponse<string>` completo,
   * permitindo ao chamador acessar headers custom (ex.: X-Relatorio-*).
   * Coexiste com `readText` para não impactar callsites legados que
   * dependem apenas do body.
   */
  readTextWithResponse(endpoint: string): Observable<HttpResponse<string>> {
    return this.http.get(`${this.baseUrl}${endpoint}`, {
      observe: 'response',
      responseType: 'text',
    }).pipe(
      catchError((error) => this.errorHandler(error))
    );
  }

  create<T>(endpoint: string, data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}${endpoint}`, data, httpOptions).pipe(
      map((res) => res),
      catchError((error) => this.errorHandler(error))
    );
  }

  update<T>(endpoint: string, data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}${endpoint}`, data, httpOptions).pipe(
      map((res) => res),
      catchError((error) => this.errorHandler(error))
    );
  }

  /** PUT "fire-and-forget": envia a requisição e resolve após o timeout sem esperar resposta completa.
   *  Útil quando o backend retorna payload enorme na resposta (ex: entidade com logo base64). */
  updateFireAndForget(endpoint: string, data: any, timeoutMs: number = 10000): Observable<any> {
    return this.http.put(`${this.baseUrl}${endpoint}`, data, httpOptions).pipe(
      timeout(timeoutMs),
      map((res) => res),
      catchError((error) => {
        // Se o erro for timeout, significa que a requisição foi enviada com sucesso
        // mas a resposta é muito grande para ser recebida a tempo.
        if (error.name === 'TimeoutError') {
          return new Observable(subscriber => {
            subscriber.next({ success: true, timedOut: true });
            subscriber.complete();
          });
        }
        // Se o backend respondeu 200 mas o parse falhou
        if (error.status === 200 || error.status === 0) {
          return new Observable(subscriber => {
            subscriber.next({ success: true });
            subscriber.complete();
          });
        }
        return this.errorHandler(error);
      })
    );
  }

  delete<T>(endpoint: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}${endpoint}`, {
      headers: httpOptions.headers,
      responseType: 'text',
    }).pipe(
      map((res) => res),
      catchError((error) => this.errorHandler(error))
    );
  }

  buildQueryParams(data?: ApiQueryParamsDTO): string {
    return data
      ? Object.entries(data)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => {
          if (key === 'filter') return value;
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&')
      : '';
  }

  patch<T>(endpoint: string, data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}${endpoint}`, data, httpOptions).pipe(
      map((res) => res),
      catchError((error) => this.errorHandler(error))
    );
  }

  // No ApiService, adicione este método:
  patchFormData(endpoint: string, formData: FormData): Observable<any> {
    // IMPORTANTE: Não usar httpOptions - o browser define Content-Type automaticamente
    return this.http.patch(`${this.baseUrl}${endpoint}`, formData).pipe(
      map((res) => res),
      catchError((error) => this.errorHandler(error))
    );
  }

  postText<T>(endpoint: string, data?: any): Observable<string> {
    return this.http.post(`${this.baseUrl}${endpoint}`, data || {}, {
      headers: httpOptions.headers,
      responseType: 'text'
    }).pipe(
      catchError((error) => this.errorHandler(error))
    );
  }

  /**
   * POST (responseType text) com opt-out do contexto de cliente do contador:
   * marca a request com `SEM_CONTEXTO_CONTADOR` para que o
   * `ContadorContextoInterceptor` NÃO injete `X-Empresa-Contexto`. Usar quando o
   * contador cria um recurso na PRÓPRIA empresa (ex.: venda do faturamento de
   * parcelas) numa rota que, por padrão, seria capturada pelo cliente ativo
   * (`/api/nota` está na allowlist do interceptor).
   */
  postTextSemContextoContador<T>(endpoint: string, data?: any): Observable<string> {
    return this.http.post(`${this.baseUrl}${endpoint}`, data || {}, {
      headers: httpOptions.headers,
      responseType: 'text',
      context: new HttpContext().set(SEM_CONTEXTO_CONTADOR, true),
    }).pipe(
      catchError((error) => this.errorHandler(error))
    );
  }

  /** PUT que retorna texto simples (não JSON). Usar quando o backend retorna string direta. */
  updateText(endpoint: string, data?: any): Observable<string> {
    return this.http.put(`${this.baseUrl}${endpoint}`, data || {}, {
      headers: httpOptions.headers,
      responseType: 'text'
    }).pipe(
      catchError((error) => this.errorHandler(error))
    );
  }
}
